/**
 * RuleResolutionModal - Mandatory rule compliance resolution
 * 
 * PLATFORM LAW:
 * - Rules always fire when conditions are met
 * - Rules always create a materialisation preview
 * - Rule outcome must be: executed or explicitly removed with reason
 * - All actions are logged. Financial impact visible everywhere.
 * 
 * This is NOT a suggestion modal. This is a compliance selector.
 * Operator choice is allowed ONLY within rule-defined bounds.
 * 
 * EF-RULE-CONFIG-02: Supports multi-select for rules like Travel
 */
import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { 
  AlertTriangle,
  CheckCircle2,
  Clock, 
  Users, 
  Loader2, 
  Check,
  X,
  Utensils,
  UserCheck,
  Scale,
  Plane,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getTaskDefinitions, createShiftLineItem } from "../../lib/api";
import { useFormatters } from "../shared/useFormatters";

// Rule type icons and colors
const RULE_TYPE_CONFIG = {
  meal: {
    icon: Utensils,
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    label: "Meal Allowance Required",
  },
  crew_chief: {
    icon: UserCheck,
    color: "text-teal-500",
    bgColor: "bg-teal-500/10",
    borderColor: "border-teal-500/30",
    label: "Crew Chief Required",
  },
  travel: {
    icon: Plane,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    label: "Travel Requirements",
  },
  default: {
    icon: Scale,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    label: "Rule Compliance Required",
  },
};

// Determine rule type from rule data
function getRuleType(rule) {
  const name = (rule.rule_name || rule.name || "").toLowerCase();
  const group = (rule.supplement_group || "").toLowerCase();
  if (name.includes("meal") || name.includes("break") || group === "meal") return "meal";
  if (name.includes("crew chief") || name.includes("leader") || group === "crew_chief") return "crew_chief";
  if (name.includes("travel") || group === "travel") return "travel";
  return "default";
}

// Preset removal reasons
const REMOVAL_REASONS = [
  { value: "client_provided", label: "Client provided meals/services" },
  { value: "contract_override", label: "Contract specifies alternative arrangement" },
  { value: "crew_compensated", label: "Crew already compensated via other means" },
  { value: "exceptional_conditions", label: "Exceptional site/operational conditions" },
  { value: "other", label: "Other (specify below)" },
];

// Single rule resolution item - supports both single and multi-select
function RuleResolutionItem({ 
  rule, 
  taskDefinitions,
  selectedTaskDefId,      // For single select: string ID
  selectedTaskDefIds,     // For multi-select: array of IDs
  onTaskDefChange,        // For single select
  onTaskDefIdsChange,     // For multi-select
  quantities,             // Object mapping taskDefId to quantity (for multi-select)
  onQuantityChange,
  onMultiQuantityChange,  // For multi-select quantity changes
  formatCurrency,
  isFixed,
  onRemove,
}) {
  const ruleType = getRuleType(rule);
  const config = RULE_TYPE_CONFIG[ruleType];
  const Icon = config.icon;
  const allowMultiple = rule.allow_multiple ?? false;

  // Get the selected task definition(s)
  const selectedTaskDef = !allowMultiple 
    ? taskDefinitions.find(td => td.id === selectedTaskDefId)
    : null;
  
  // For multi-select, calculate total from all selected items
  const selectedDefs = allowMultiple
    ? taskDefinitions.filter(td => (selectedTaskDefIds || []).includes(td.id))
    : [];
  
  // Calculate total
  const total = allowMultiple
    ? selectedDefs.reduce((sum, td) => {
        const qty = quantities?.[td.id] || 1;
        return sum + (td.default_charge_rate || 0) * qty;
      }, 0)
    : (selectedTaskDef?.default_charge_rate || 0) * (quantities?.[selectedTaskDefId] || 1);

  // Get allowed options for this rule
  const allowedOptions = rule.allowed_task_definition_ids?.length > 0
    ? taskDefinitions.filter(td => rule.allowed_task_definition_ids.includes(td.id))
    : taskDefinitions;

  // Handle checkbox toggle for multi-select
  const handleCheckboxToggle = (taskDefId, checked) => {
    if (!onTaskDefIdsChange) return;
    const current = selectedTaskDefIds || [];
    if (checked) {
      onTaskDefIdsChange([...current, taskDefId]);
    } else {
      onTaskDefIdsChange(current.filter(id => id !== taskDefId));
    }
  };

  return (
    <div
      className={cn(
        "p-4 rounded-lg border",
        config.bgColor,
        config.borderColor,
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-5 w-5", config.color)} />
          <span className="font-semibold">{config.label}</span>
          <Badge variant="destructive" className="text-xs">
            Required
          </Badge>
          {allowMultiple && (
            <Badge variant="outline" className="text-xs">
              Multi-select
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <X className="h-4 w-4 mr-1" />
          Remove
        </Button>
      </div>

      {/* Rule message */}
      <p className="text-sm text-muted-foreground mb-4">
        {rule.message || rule.prompt_text}
      </p>

      {/* Resolution fields */}
      <div className="space-y-3">
        {/* Single Select Mode */}
        {!allowMultiple && (
          <>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">
                {isFixed ? "Task Definition (Auto-selected)" : "Select Compliant Task Definition *"}
              </Label>
              <Select 
                value={selectedTaskDefId} 
                onValueChange={onTaskDefChange}
                disabled={isFixed}
              >
                <SelectTrigger className={cn("text-sm h-10", !selectedTaskDefId && "border-destructive")}>
                  <SelectValue placeholder="Select required task..." />
                </SelectTrigger>
                <SelectContent 
                  position="popper" 
                  side="top"
                  className="max-h-[350px] overflow-y-auto"
                >
                  {allowedOptions.map((td) => (
                    <SelectItem key={td.id} value={td.id}>
                      <div className="flex items-center justify-between gap-4 w-full">
                        <span>{td.name}</span>
                        <span className="text-muted-foreground text-xs">
                          {formatCurrency(td.default_charge_rate || 0)}/unit
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedTaskDefId && (
                <p className="text-xs text-destructive mt-1">Selection required</p>
              )}
            </div>

            {/* Quantity - locked for crew_count mode */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">
                Quantity {rule.quantity_mode === "crew_count" && "(Based on crew count)"}
              </Label>
              <Input
                type="number"
                min="1"
                value={quantities?.[selectedTaskDefId] || 1}
                onChange={(e) => onQuantityChange(parseInt(e.target.value) || 1)}
                className="text-sm h-10"
                disabled={rule.quantity_mode === "crew_count"}
              />
            </div>

            {/* Financial Preview */}
            {selectedTaskDef && (
              <div className="bg-white/10 rounded p-3 flex items-center justify-between">
                <span className="text-sm">
                  {selectedTaskDef.name} x {quantities?.[selectedTaskDefId] || 1}
                </span>
                <span className="font-semibold text-teal-400">
                  {formatCurrency(total)}
                </span>
              </div>
            )}
          </>
        )}

        {/* Multi Select Mode */}
        {allowMultiple && (
          <>
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">
                Select applicable supplements (choose one or more) *
              </Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {allowedOptions.map((td) => {
                  const isSelected = (selectedTaskDefIds || []).includes(td.id);
                  const qty = quantities?.[td.id] || 1;
                  return (
                    <div 
                      key={td.id}
                      className={cn(
                        "flex items-center gap-3 p-2 rounded border transition-colors",
                        isSelected ? "border-teal-500 bg-teal-500/10" : "border-border hover:border-muted-foreground"
                      )}
                    >
                      <Checkbox
                        id={`task-${td.id}`}
                        checked={isSelected}
                        onCheckedChange={(checked) => handleCheckboxToggle(td.id, checked)}
                      />
                      <div className="flex-1 min-w-0">
                        <label 
                          htmlFor={`task-${td.id}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {td.name}
                        </label>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(td.default_charge_rate || 0)}/unit
                        </p>
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground">Qty:</Label>
                          <Input
                            type="number"
                            min="1"
                            value={qty}
                            onChange={(e) => onMultiQuantityChange?.(td.id, parseInt(e.target.value) || 1)}
                            className="w-16 h-8 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {(!selectedTaskDefIds || selectedTaskDefIds.length === 0) && (
                <p className="text-xs text-destructive mt-1">At least one selection required</p>
              )}
            </div>

            {/* Multi-select Financial Preview */}
            {selectedDefs.length > 0 && (
              <div className="bg-white/10 rounded p-3 space-y-2">
                {selectedDefs.map(td => {
                  const qty = quantities?.[td.id] || 1;
                  const itemTotal = (td.default_charge_rate || 0) * qty;
                  return (
                    <div key={td.id} className="flex items-center justify-between text-sm">
                      <span>{td.name} x {qty}</span>
                      <span className="text-teal-400">{formatCurrency(itemTotal)}</span>
                    </div>
                  );
                })}
                <div className="border-t border-white/20 pt-2 flex items-center justify-between">
                  <span className="font-medium">Total</span>
                  <span className="font-semibold text-teal-400">{formatCurrency(total)}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Removal confirmation dialog
function RemovalDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  rule,
  submitting,
}) {
  const [reasonCategory, setReasonCategory] = useState("");
  const [reasonText, setReasonText] = useState("");
  const ruleType = getRuleType(rule);
  const config = RULE_TYPE_CONFIG[ruleType];

  const isValid = reasonText.trim().length >= 10;

  const handleConfirm = () => {
    const fullReason = reasonCategory === "other" || !reasonCategory
      ? reasonText.trim()
      : `${REMOVAL_REASONS.find(r => r.value === reasonCategory)?.label}: ${reasonText.trim()}`;
    onConfirm(fullReason);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            Remove Rule Requirement
          </DialogTitle>
          <DialogDescription>
            This action requires justification and will be logged for audit.
          </DialogDescription>
        </DialogHeader>

        <div className={cn("p-3 rounded-lg", config.bgColor, config.borderColor, "border")}>
          <p className="text-sm font-medium">{config.label}</p>
          <p className="text-xs text-muted-foreground">{rule.message}</p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm mb-2 block">Reason Category</Label>
            <Select value={reasonCategory} onValueChange={setReasonCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason category..." />
              </SelectTrigger>
              <SelectContent>
                {REMOVAL_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm mb-2 block">
              Detailed Explanation * <span className="text-muted-foreground">(min 10 characters)</span>
            </Label>
            <Textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Explain why this rule requirement is being removed..."
              rows={3}
              className={cn(!isValid && reasonText.length > 0 && "border-destructive")}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {reasonText.length}/10 minimum characters
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isValid || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Removing...
              </>
            ) : (
              "Remove with Reason"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RuleResolutionModal({
  isOpen,
  onClose,
  rules = [], // Rules that fired and require resolution
  shiftId,
  shiftData = {}, // Shift info for context (duration, crew count)
  onSuccess,
}) {
  const { formatCurrency } = useFormatters();
  const [taskDefinitions, setTaskDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Track rule resolutions
  const [resolutions, setResolutions] = useState({});
  
  // Removal dialog state
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const [ruleToRemove, setRuleToRemove] = useState(null);
  const [removedRules, setRemovedRules] = useState({});

  // Safe exit handler - clears all temporary state and closes modal
  // RULE ENGINE SAFETY: Cancelling means NO rules are executed, NO supplements added
  const handleSafeExit = () => {
    if (submitting) return; // Prevent close during submission
    
    // Clear all temporary state
    setResolutions({});
    setRemovedRules({});
    setRuleToRemove(null);
    setRemovalDialogOpen(false);
    
    // Close modal without executing any rules
    onClose();
  };

  // Initialize resolutions from rules
  useEffect(() => {
    if (rules.length > 0 && Object.keys(resolutions).length === 0) {
      const initial = {};
      rules.forEach(r => {
        const allowMultiple = r.allow_multiple ?? false;
        initial[r.rule_id] = {
          taskDefId: r.effect_type === "fixed" ? r.task_definition_id : null,
          taskDefIds: [], // For multi-select
          quantities: {}, // Map of taskDefId -> quantity
          quantity: r.quantity_mode === "crew_count" 
            ? (shiftData.staff_required || r.resolved_quantity || 1) 
            : (r.quantity || 1),
          allowMultiple,
        };
      });
      setResolutions(initial);
    }
  }, [rules, shiftData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setResolutions({});
      setRemovedRules({});
      setRuleToRemove(null);
      setRemovalDialogOpen(false);
    }
  }, [isOpen]);

  // Load allowed task definitions
  useEffect(() => {
    const loadTaskDefs = async () => {
      setLoading(true);
      try {
        // EF-RULE-OPTION-LISTS-FIX: Fetch all task definitions (not default 100)
        // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1: Only enabled
        const response = await getTaskDefinitions({ limit: 500, enabled_filter: "enabled" });
        setTaskDefinitions(response.data || []);
        
        // Auto-select for fixed effect_type rules
        rules.forEach(r => {
          if (r.effect_type === "fixed" && r.task_definition_id) {
            setResolutions(prev => ({
              ...prev,
              [r.rule_id]: {
                ...prev[r.rule_id],
                taskDefId: r.task_definition_id,
              },
            }));
          }
        });
      } catch (err) {
        console.error("Failed to load task definitions:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && rules.length > 0) {
      loadTaskDefs();
    }
  }, [isOpen, rules]);

  // Update task definition selection (single select)
  const handleTaskDefChange = (ruleId, taskDefId) => {
    setResolutions(prev => ({
      ...prev,
      [ruleId]: {
        ...prev[ruleId],
        taskDefId,
        quantities: {
          ...prev[ruleId]?.quantities,
          [taskDefId]: prev[ruleId]?.quantity || 1,
        },
      },
    }));
  };

  // Update task definition selection (multi-select)
  const handleTaskDefIdsChange = (ruleId, taskDefIds) => {
    setResolutions(prev => {
      const currentQuantities = prev[ruleId]?.quantities || {};
      // Initialize quantity to 1 for newly selected items
      const updatedQuantities = { ...currentQuantities };
      taskDefIds.forEach(id => {
        if (!updatedQuantities[id]) {
          updatedQuantities[id] = 1;
        }
      });
      return {
        ...prev,
        [ruleId]: {
          ...prev[ruleId],
          taskDefIds,
          quantities: updatedQuantities,
        },
      };
    });
  };

  // Update quantity (single select)
  const handleQuantityChange = (ruleId, quantity) => {
    setResolutions(prev => {
      const taskDefId = prev[ruleId]?.taskDefId;
      return {
        ...prev,
        [ruleId]: {
          ...prev[ruleId],
          quantity: Math.max(1, quantity),
          quantities: taskDefId ? {
            ...prev[ruleId]?.quantities,
            [taskDefId]: Math.max(1, quantity),
          } : prev[ruleId]?.quantities,
        },
      };
    });
  };

  // Update quantity for specific task def (multi-select)
  const handleMultiQuantityChange = (ruleId, taskDefId, quantity) => {
    setResolutions(prev => ({
      ...prev,
      [ruleId]: {
        ...prev[ruleId],
        quantities: {
          ...prev[ruleId]?.quantities,
          [taskDefId]: Math.max(1, quantity),
        },
      },
    }));
  };

  // Open removal dialog
  const handleRemoveClick = (rule) => {
    setRuleToRemove(rule);
    setRemovalDialogOpen(true);
  };

  // Confirm removal with reason
  const handleRemovalConfirm = async (reason) => {
    if (!ruleToRemove) return;
    
    setSubmitting(true);
    try {
      // Log the removal (in production, this would call an API)
      console.log("Rule removal logged:", {
        rule_id: ruleToRemove.rule_id,
        rule_name: ruleToRemove.rule_name,
        reason,
        shift_id: shiftId,
        timestamp: new Date().toISOString(),
      });
      
      setRemovedRules(prev => ({
        ...prev,
        [ruleToRemove.rule_id]: { reason, removedAt: new Date().toISOString() },
      }));
      setRemovalDialogOpen(false);
      setRuleToRemove(null);
    } catch (err) {
      console.error("Failed to log removal:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate totals for active rules
  const { activeRules, totalAmount, allResolved } = useMemo(() => {
    const active = rules.filter(r => !removedRules[r.rule_id]);
    let total = 0;
    let resolved = true;

    active.forEach(r => {
      const res = resolutions[r.rule_id];
      const allowMultiple = r.allow_multiple ?? false;
      
      if (allowMultiple) {
        // Multi-select: Check if at least one item is selected
        if (!res?.taskDefIds || res.taskDefIds.length === 0) {
          resolved = false;
        } else {
          // Calculate total for all selected items
          res.taskDefIds.forEach(taskDefId => {
            const taskDef = taskDefinitions.find(td => td.id === taskDefId);
            if (taskDef) {
              const qty = res.quantities?.[taskDefId] || 1;
              total += (taskDef.default_charge_rate || 0) * qty;
            }
          });
        }
      } else {
        // Single select: Check if task is selected
        if (!res?.taskDefId) {
          resolved = false;
        } else {
          const taskDef = taskDefinitions.find(td => td.id === res.taskDefId);
          if (taskDef) {
            const qty = res.quantities?.[res.taskDefId] || res.quantity || 1;
            total += (taskDef.default_charge_rate || 0) * qty;
          }
        }
      }
    });

    return { activeRules: active, totalAmount: total, allResolved: resolved };
  }, [rules, removedRules, resolutions, taskDefinitions]);

  // Handle confirm and execute
  // EF-RULE-TASKDEFINITION-BINDING: Use exact TaskDefinition ID from selection
  const handleConfirm = async () => {
    if (!allResolved) return;
    
    setSubmitting(true);
    try {
      const executedItems = [];
      
      for (const rule of activeRules) {
        const res = resolutions[rule.rule_id];
        const allowMultiple = rule.allow_multiple ?? false;
        
        if (allowMultiple) {
          // Multi-select: Create task for each selected item
          const taskDefIds = res?.taskDefIds || [];
          for (const taskDefId of taskDefIds) {
            const taskDef = taskDefinitions.find(td => td.id === taskDefId);
            if (!taskDef) continue;
            
            const qty = res.quantities?.[taskDefId] || 1;
            const taskData = {
              shift_id: shiftId,
              task_definition_id: taskDefId, // EXACT TaskDefinition ID from selection
              quantity: qty,
              charge_rate: taskDef.default_charge_rate || 0,
              pay_rate: taskDef.default_pay_rate || 0,
              rule_added: true,
              rule_id: rule.rule_id,
              rule_name: rule.rule_name || rule.name,
              notes: `Confirmed via rule: ${rule.rule_name || rule.name}`,
            };
            
            // EF-RULE-PREVIEW-SPLIT: Only create if shiftId exists
            // If shiftId is null (preview mode), just collect selections for later
            if (shiftId) {
              // EF-RULE-TASKDEFINITION-BINDING: Use createShiftLineItem for proper table
              await createShiftLineItem({
                shift_id: shiftId,
                task_definition_id: taskDefId, // EXACT ID - no fallback lookup
                quantity: qty,
                rule_added: true,
                rule_id: rule.rule_id,
                rule_name: rule.rule_name || rule.name,
              });
            }
            
            executedItems.push({
              ...taskData,
              task_definition_name: taskDef.name,
              rule_name: rule.rule_name,
            });
          }
        } else {
          // Single select: Create single task
          if (!res?.taskDefId) continue;
          
          const taskDef = taskDefinitions.find(td => td.id === res.taskDefId);
          if (!taskDef) continue;
          
          const qty = res.quantities?.[res.taskDefId] || res.quantity || 1;
          const taskData = {
            shift_id: shiftId,
            task_definition_id: res.taskDefId, // EXACT TaskDefinition ID from selection
            quantity: qty,
            charge_rate: taskDef.default_charge_rate || 0,
            pay_rate: taskDef.default_pay_rate || 0,
            rule_added: true,
            rule_id: rule.rule_id,
            rule_name: rule.rule_name || rule.name,
            notes: `Confirmed via rule: ${rule.rule_name || rule.name}`,
          };
          
          // EF-RULE-PREVIEW-SPLIT: Only create if shiftId exists
          if (shiftId) {
            // EF-RULE-TASKDEFINITION-BINDING: Use createShiftLineItem for proper table
            await createShiftLineItem({
              shift_id: shiftId,
              task_definition_id: res.taskDefId, // EXACT ID - no fallback lookup
              quantity: qty,
              rule_added: true,
              rule_id: rule.rule_id,
              rule_name: rule.rule_name || rule.name,
            });
          }
          
          executedItems.push({
            ...taskData,
            task_definition_name: taskDef.name,
            rule_name: rule.rule_name,
          });
        }
      }

      onSuccess?.({
        executed: executedItems,
        removed: Object.entries(removedRules).map(([id, data]) => ({
          rule_id: id,
          ...data,
        })),
        // EF-RULE-PREVIEW-SPLIT: Pass resolutions for deferred creation
        resolutions: resolutions,
      });
      onClose();
    } catch (err) {
      console.error("Failed to execute rules:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || rules.length === 0) return null;

  return (
    <>
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          if (!open) handleSafeExit();
        }}
      >
        <DialogContent 
          className="max-w-lg" 
          data-testid="rule-resolution-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <Scale className="h-5 w-5" />
              Rule Compliance Required
            </DialogTitle>
            <DialogDescription>
              The following rules have been triggered and require resolution before proceeding.
              Select a compliant option for each or provide justification for removal.
            </DialogDescription>
          </DialogHeader>

          {/* Shift context info */}
          <div className="flex gap-4 text-sm text-muted-foreground bg-surface-elevated/20 rounded-lg p-3">
            {shiftData.duration_hours && (
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{shiftData.duration_hours}h shift</span>
              </div>
            )}
            {shiftData.staff_required && (
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{shiftData.staff_required} crew required</span>
              </div>
            )}
          </div>

          {/* Rules list */}
          <div className="space-y-4 max-h-[400px] overflow-y-auto py-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {activeRules.map((rule) => (
                  <RuleResolutionItem
                    key={rule.rule_id}
                    rule={rule}
                    taskDefinitions={taskDefinitions}
                    selectedTaskDefId={resolutions[rule.rule_id]?.taskDefId || ""}
                    selectedTaskDefIds={resolutions[rule.rule_id]?.taskDefIds || []}
                    onTaskDefChange={(id) => handleTaskDefChange(rule.rule_id, id)}
                    onTaskDefIdsChange={(ids) => handleTaskDefIdsChange(rule.rule_id, ids)}
                    quantities={resolutions[rule.rule_id]?.quantities || {}}
                    onQuantityChange={(qty) => handleQuantityChange(rule.rule_id, qty)}
                    onMultiQuantityChange={(taskDefId, qty) => handleMultiQuantityChange(rule.rule_id, taskDefId, qty)}
                    formatCurrency={formatCurrency}
                    isFixed={rule.effect_type === "fixed"}
                    onRemove={() => handleRemoveClick(rule)}
                  />
                ))}
                
                {/* Removed rules summary */}
                {Object.keys(removedRules).length > 0 && (
                  <div className="border border-destructive/30 bg-destructive/10 rounded-lg p-3">
                    <p className="text-sm font-medium text-destructive mb-2">Removed Rules (Logged)</p>
                    {Object.entries(removedRules).map(([ruleId, data]) => {
                      const rule = rules.find(r => r.rule_id === ruleId);
                      return (
                        <div key={ruleId} className="text-xs text-muted-foreground">
                          <span className="font-medium">{rule?.rule_name}:</span> {data.reason}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Total preview */}
          {activeRules.length > 0 && (
            <div className={cn(
              "rounded-lg p-3 flex items-center justify-between",
              allResolved ? "bg-teal-500/10 border border-teal-500/30" : "bg-amber-500/10 border border-amber-500/30"
            )}>
              <div className="flex items-center gap-2">
                {allResolved ? (
                  <CheckCircle2 className="h-4 w-4 text-teal-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                )}
                <span className="text-sm font-medium">
                  {allResolved 
                    ? `${activeRules.length} rule${activeRules.length > 1 ? "s" : ""} resolved` 
                    : "Selection required for all rules"}
                </span>
              </div>
              <span className="text-lg font-semibold text-teal-400">
                {formatCurrency(totalAmount)}
              </span>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleSafeExit}
              disabled={submitting}
              className="flex-1 sm:flex-none"
              data-testid="cancel-resolution-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting || (activeRules.length > 0 && !allResolved)}
              className="bg-teal-600 hover:bg-teal-700 flex-1 sm:flex-none"
              data-testid="confirm-resolution-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm & Execute Rules
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Removal confirmation dialog */}
      {ruleToRemove && (
        <RemovalDialog
          isOpen={removalDialogOpen}
          onClose={() => {
            setRemovalDialogOpen(false);
            setRuleToRemove(null);
          }}
          onConfirm={handleRemovalConfirm}
          rule={ruleToRemove}
          submitting={submitting}
        />
      )}
    </>
  );
}
